import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { LineChart, Line, XAxis, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import MIHeader from '../MIHeader';
import { fmtNum, fmtMonth, pctChange, slugify, shiftYear, getLatestPeriod } from '../utils';
import { Helmet } from 'react-helmet';
import { useTranslation } from '../../../hooks/useTranslation';
import { localizedFactoryName, localizedStateName, localizedDistrictName } from '../../../utils/localizedDisplayName';

const StatePage = () => {
  const { t, language } = useTranslation();
  const { stateSlug } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stateName, setStateName] = useState(null);
  const [latestMonth, setLatestMonth] = useState(null);
  const [total, setTotal] = useState(0);
  const [prevMonthTotal, setPrevMonthTotal] = useState(null);
  const [prevYearTotal, setPrevYearTotal] = useState(null);
  const [nationalRank, setNationalRank] = useState(null);
  const [totalStates, setTotalStates] = useState(0);
  const [districts, setDistricts] = useState([]);
  const [topFactories, setTopFactories] = useState([]);
  const [trend, setTrend] = useState([]);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const latest = await getLatestPeriod();
        if (!isMounted) return;
        setLatestMonth(latest);

        const { data: stateRows, error: sErr } = await supabase
          .from('mi_v_state_monthly')
          .select('canonical_state, total_allocated_mt')
          .eq('period_month', latest)
          .order('total_allocated_mt', { ascending: false });
        if (sErr) throw sErr;

        const rankIdx = (stateRows || []).findIndex((r) => slugify(r.canonical_state) === stateSlug);
        if (rankIdx === -1) throw new Error('State not found');
        const thisState = stateRows[rankIdx];

        const { data: prevMonthRow } = await supabase
          .from('mi_v_state_monthly')
          .select('total_allocated_mt')
          .eq('canonical_state', thisState.canonical_state)
          .lt('period_month', latest)
          .order('period_month', { ascending: false })
          .limit(1)
          .maybeSingle();

        const { data: prevYearRow } = await supabase
          .from('mi_v_state_monthly')
          .select('total_allocated_mt')
          .eq('canonical_state', thisState.canonical_state)
          .eq('period_month', shiftYear(latest, -1))
          .maybeSingle();

        const { data: trendRows } = await supabase
          .from('mi_v_state_monthly')
          .select('period_month, total_allocated_mt')
          .eq('canonical_state', thisState.canonical_state)
          .order('period_month', { ascending: false })
          .limit(12);

        const { data: districtRows } = await supabase
          .from('mi_v_district_monthly')
          .select('canonical_district, total_allocated_mt, mills_active, mills_reporting')
          .eq('canonical_state', thisState.canonical_state)
          .eq('period_month', latest)
          .order('total_allocated_mt', { ascending: false });

        const { data: factoryRows } = await supabase
          .from('mi_v_release_orders_canonical')
          .select('canonical_mill_name, canonical_district, allocated_qty_mt')
          .eq('period_month', latest)
          .eq('canonical_state', thisState.canonical_state)
          .order('allocated_qty_mt', { ascending: false })
          .limit(5);

        if (!isMounted) return;
        setStateName(thisState.canonical_state);
        setTotal(Number(thisState.total_allocated_mt));
        setNationalRank(rankIdx + 1);
        setTotalStates(stateRows.length);
        setPrevMonthTotal(prevMonthRow ? Number(prevMonthRow.total_allocated_mt) : null);
        setPrevYearTotal(prevYearRow ? Number(prevYearRow.total_allocated_mt) : null);
        setTrend((trendRows || []).slice().reverse().map((r) => ({
          month: new Date(r.period_month).toLocaleDateString('en-IN', { month: 'short' }),
          mt: Number(r.total_allocated_mt),
        })));
        setDistricts(districtRows || []);
        setTopFactories(factoryRows || []);
      } catch (e) {
        if (isMounted) setError(e.message);
      } finally {
        if (isMounted) setLoading(false);
      }
    })();
    return () => { isMounted = false; };
  }, [stateSlug]);

  const momPct = pctChange(total, prevMonthTotal);
  const yoyPct = pctChange(total, prevYearTotal);

  return (
    <div className="min-h-screen bg-[var(--bg-primary)]">
      <Helmet>
        <title>{stateName ? `${stateName} Sugar Quota Data — SugarHathi Market Intelligence` : 'State Sugar Quota Data | SugarHathi Market Intelligence'}</title>
        <meta name="description" content={stateName ? `Live sugar quota release-order data for ${stateName}. Monthly mill-wise and district-wise allocation figures, verified by SugarHathi.` : 'Live state-wise sugar quota release-order data across India, verified by SugarHathi.'} />
      </Helmet>
      <MIHeader subtitle={stateName ? localizedStateName(stateName, language) : t('marketIntelligence.stateFallback', 'State')} />
      <main className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-2xl py-8">
        {loading && (
          <div className="flex flex-col items-center justify-center py-24 text-[var(--text-muted)]">
            <Icon name="Loader" size={32} className="animate-spin mb-3" />
            <p>{t('common.loading', 'Loading...')}</p>
          </div>
        )}
        {!loading && error && (
          <div className="flex flex-col items-center justify-center py-24 text-[var(--text-muted)]">
            <Icon name="AlertTriangle" size={32} className="mb-3 text-[var(--error)]" />
            <p>{error}</p>
          </div>
        )}
        {!loading && !error && stateName && (
          <>
            <h1 className="text-2xl font-black text-[var(--text-primary)] mb-1">{localizedStateName(stateName, language)}</h1>
            <p className="text-sm text-[var(--text-muted)] mb-6">{t('marketIntelligence.completeMonthNote', 'Last complete month')} — {fmtMonth(latestMonth)}</p>

            <div className="grid grid-cols-2 gap-3 mb-6">
              <div className="bg-[var(--bg-card)] border border-[var(--border-light)] rounded-[var(--radius-card)] p-4">
                <p className="text-xs text-[var(--text-muted)] mb-1">{t('marketIntelligence.totalAllocation', 'Total allocation')}</p>
                <p className="text-xl font-black text-[var(--text-primary)]">{fmtNum(total)} MT</p>
                {momPct !== null && (
                  <p className="text-xs mt-1" style={{ color: momPct >= 0 ? 'var(--success)' : 'var(--error)' }}>{momPct >= 0 ? t('marketIntelligence.momUp', '{{pct}}% up MoM', { pct: Math.abs(momPct).toFixed(1) }) : t('marketIntelligence.momDown', '{{pct}}% down MoM', { pct: Math.abs(momPct).toFixed(1) })}</p>
                )}
              </div>
              <div className="bg-[var(--bg-card)] border border-[var(--border-light)] rounded-[var(--radius-card)] p-4">
                <p className="text-xs text-[var(--text-muted)] mb-1">{t('marketIntelligence.nationalRank', 'National rank')}</p>
                <p className="text-xl font-black text-[var(--text-primary)]">#{nationalRank} <span className="text-xs font-medium text-[var(--text-muted)]">{t('marketIntelligence.ofCount', 'of {{count}}', { count: totalStates })}</span></p>
                {yoyPct !== null && (
                  <p className="text-xs mt-1" style={{ color: Math.abs(yoyPct) < 1 ? 'var(--text-muted)' : yoyPct >= 0 ? 'var(--success)' : 'var(--error)' }}>
                    {Math.abs(yoyPct) < 1 ? t('marketIntelligence.yoyFlat', 'flat YoY') : (yoyPct >= 0 ? t('marketIntelligence.yoyUp', '{{pct}}% up YoY', { pct: Math.abs(yoyPct).toFixed(1) }) : t('marketIntelligence.yoyDown', '{{pct}}% down YoY', { pct: Math.abs(yoyPct).toFixed(1) }))}
                  </p>
                )}
              </div>
            </div>

            {trend.length > 1 && (
              <div className="mb-6">
                <p className="text-sm font-semibold text-[var(--text-primary)] mb-2">{t('marketIntelligence.trendLast12Months', 'Trend, last 12 months')}</p>
                <div className="h-28">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={trend}>
                      <XAxis dataKey="month" stroke="#9CA8BA" fontSize={10} tickLine={false} axisLine={false} />
                      <Line type="monotone" dataKey="mt" stroke="#F59E0B" strokeWidth={2} dot={{ r: 3, fill: '#F59E0B' }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            <p className="text-sm font-semibold text-[var(--text-primary)] mb-2">{t('marketIntelligence.districtsCount', 'Districts ({{count}})', { count: districts.length })}</p>
            <div className="mb-6">
              {districts.map((d, i) => (
                <button
                  key={i}
                  onClick={() => navigate(`/market-intelligence/district/${slugify(d.canonical_district)}`)}
                  className="w-full grid grid-cols-[1fr_auto_auto] gap-3 py-2.5 border-b border-[var(--border-light)] text-left hover:bg-[var(--bg-surface)] transition-colors items-center"
                >
                  <span className="text-sm text-[var(--text-primary)] truncate">{localizedDistrictName(d.canonical_district, stateName, language)}</span>
                  <span className="text-xs text-[var(--text-muted)] whitespace-nowrap">{t('marketIntelligence.districtActiveSlash', '{{active}}/{{reporting}} active', { active: d.mills_active, reporting: d.mills_reporting })}</span>
                  <span className="text-sm text-[var(--text-primary)] font-medium whitespace-nowrap">{fmtNum(d.total_allocated_mt)}</span>
                </button>
              ))}
            </div>

            <p className="text-sm font-semibold text-[var(--text-primary)] mb-2">{t('marketIntelligence.topFactories', 'Top factories')}</p>
            <div className="space-y-2">
              {topFactories.map((f, i) => (
                <button
                  key={i}
                  onClick={() => navigate(`/market-intelligence/factory/${slugify(f.canonical_mill_name)}`)}
                  className="w-full flex items-center justify-between bg-[var(--bg-card)] border border-[var(--border-light)] rounded-[var(--radius-input)] px-3 py-2.5 text-left hover:bg-[var(--bg-surface)] transition-colors"
                >
                  <div className="min-w-0 pr-2">
                    <p className="text-sm text-[var(--text-primary)] truncate">{localizedFactoryName(f, language, 'canonical_mill_name')}</p>
                    <span
                      role="link"
                      onClick={(e) => { e.stopPropagation(); navigate(`/market-intelligence/district/${slugify(f.canonical_district)}`); }}
                      className="text-xs text-[var(--text-muted)] hover:underline inline-block"
                    >{localizedDistrictName(f.canonical_district, stateName, language)}</span>
                  </div>
                  <span className="text-sm text-[var(--text-primary)] font-medium whitespace-nowrap">{fmtNum(f.allocated_qty_mt)} MT</span>
                </button>
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default StatePage;

